// FILE: src/lib/billingApi.js
//
// ✔ Retains ALL existing functionality:
//   - Prefers local emulator on localhost, with short timeout
//   - Falls back to prod Cloud Functions endpoint
//   - Same path shapes ("/create-checkout-session", "/create-portal-link")
//   - Same default success/cancel URLs
// ✔ Adds:
//   - Firebase ID token Authorization header (server-side auth)
//   - GET support and new endpoints: /catalog, /customer-context
//   - POST /update-subscription for safe quantity changes (no immediate charge)

import { auth } from "../firebase";

// ---- Static bases (kept from your original helper) ----
const PROJECT_ID = "bobquiz-272a7";
const REGION = "us-central1";
const FUNCTION_NAME = "api";

const BASE_LOCAL = `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}/${FUNCTION_NAME}`;
const BASE_PROD  = `https://${REGION}-${PROJECT_ID}.cloudfunctions.net/${FUNCTION_NAME}`;

// Small timeout helper so we don't hang on a dead emulator
function withTimeout(ms = 900) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort("timeout"), ms);
  return { signal: ctrl.signal, done: () => clearTimeout(t) };
}

function isLocalhost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

// Core request function that preserves emulator-first behavior and adds auth
async function request(path, { method = "POST", body } = {}) {
  const idToken = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
  const headers = {
    "Content-Type": "application/json",
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
  };

  // 1) Try emulator first — only when running on localhost
  if (isLocalhost()) {
    try {
      const t = withTimeout(900);
      const res = await fetch(`${BASE_LOCAL}${path}`, {
        method,
        headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        signal: t.signal,
      });
      t.done();
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Emulator HTTP ${res.status} ${text}`);
      }
      // GET endpoints may return empty body; guard with try/catch
      try { return await res.json(); } catch { return {}; }
    } catch (_err) {
      // fall through to prod
    }
  }

  // 2) Prod fallback
  const res = await fetch(`${BASE_PROD}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Prod HTTP ${res.status} ${text}`);
  }
  try { return await res.json(); } catch { return {}; }
}

// ---------------- Public helpers ----------------

// Create a Stripe Checkout session (subscription or one-off).
export async function createCheckoutSession({
  uid,
  email,
  mode,
  priceId,
  quantity = 1,
  success_url = `${window.location.origin}/billing/success`,
  cancel_url  = `${window.location.origin}/billing/canceled`,
  metadata = {},
}) {
  if (!uid || !email) throw new Error("Missing uid/email");
  if (!priceId) throw new Error("Missing priceId");
  if (!mode) throw new Error("Missing mode");

  return request("/create-checkout-session", {
    method: "POST",
    body: {
      uid, email, mode, priceId, quantity, success_url, cancel_url, metadata,
    },
  });
}

// Create a Stripe Billing Portal link.
export async function createPortalLink({
  uid,
  return_url = window.location.origin,
}) {
  if (!uid) throw new Error("Missing uid");
  return request("/create-portal-link", {
    method: "POST",
    body: { uid, return_url },
  });
}

// Update quantity on an existing subscription (server uses proration_behavior: 'none').
export async function updateSubscription({ uid, quantity }) {
  if (!uid) throw new Error("Missing uid");
  if (!(Number.isFinite(quantity) && quantity > 0)) throw new Error("Invalid quantity");
  return request("/update-subscription", {
    method: "POST",
    body: { uid, quantity },
  });
}

// Pull active products/prices (source of truth from Stripe).
export async function getCatalog() {
  return request("/catalog", { method: "GET" });
}

// Get user-specific context: default currency + current subscription summary.
export async function getCustomerContext() {
  return request("/customer-context", { method: "GET" });
}

/** Spend 1 quiz token for a live host session (idempotent per gameId). */
export async function spendQuizToken({ gameId }) {
  if (!gameId) throw new Error("Missing gameId");
  return request("/spend-quiz-token", {
    method: "POST",
    body: { gameId },
  });
}

/** @deprecated Automatic refunds are disabled; kept for older clients (returns 410). */
export async function refundQuizToken({ gameId, maxQuestionIndex } = {}) {
  if (!gameId) throw new Error("Missing gameId");
  const body = { gameId };
  if (maxQuestionIndex != null && Number.isFinite(Number(maxQuestionIndex))) {
    body.maxQuestionIndex = Number(maxQuestionIndex);
  }
  return request("/refund-quiz-token", {
    method: "POST",
    body,
  });
}

/** Idempotent seed of test tokens for allowlisted owner emails. */
export async function seedDevTokens({ amount } = {}) {
  return request("/seed-dev-tokens", {
    method: "POST",
    body: amount ? { amount } : {},
  });
}
